import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { reportesAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { formatCurrency, formatNumber } = useApp();
  const { usuario, esAdmin, esAdminOGerente } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [verDetalleSri, setVerDetalleSri] = useState(true);

  // ─── Control de visibilidad por permisos ───
  const tienePermiso = useCallback((permiso) => {
    if (esAdmin()) return true;
    const permisos = usuario?.permisos || [];
    if (permisos.includes('*')) return true;
    if (permisos.includes(permiso)) return true;
    const base = permiso.split(':')[0];
    if (permisos.includes(base)) return true;
    return false;
  }, [usuario?.permisos, esAdmin]);

  // Permisos financieros: solo admin/gerente ven montos globales
  const verFinanzasGlobales = tienePermiso('configuracion') || tienePermiso('reportes');
  const verInventarioValor = tienePermiso('inventario');
  const verCompras = tienePermiso('compras');
  const verGastos = tienePermiso('caja');
  const verCxC = tienePermiso('ventas');
  const verVentas = tienePermiso('ventas');
  const verOrdenes = tienePermiso('ordenes_trabajo');

  useEffect(() => {
    const load = async () => {
      setError(null);
      try {
        const res = await reportesAPI.getDashboard();
        if (res.data.success) setData(res.data.data);
      } catch (e) {
        console.error('Error cargando dashboard:', e);
        setError('Error al cargar el dashboard. Intente recargar la página.');
      }
      finally { setLoading(false); }
    };
    load();
  }, [refreshKey]);

  // FIX: Loading state shows spinner with descriptive text
  if (loading) return (
    <div className="loading-container d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
      <div className="spinner-border text-primary mb-3"></div>
      <p className="text-muted">Cargando dashboard...</p>
    </div>
  );

  // FIX: Safe destructuring with fallback to empty object when data is null
  const {
    inventario = {},
    ventas_hoy = { total: 0, cantidad: 0 },
    ventas_mes = { total: 0, cantidad: 0 },
    compras_mes = { total_compras: 0, monto_total: 0 },
    clientes = { total: 0, nuevos_mes: 0 },
    caja_activa = null,
    ordenes_pendientes = { total: 0, detalle: [] },
    ultimos_movimientos = [],
    ultimas_ventas = [],
    top_productos = [],
    top_categorias = [],
    cuentas_por_cobrar = null,
    gastos_mes = null,
    gastos_diarios_mes = null,
    ganancias = null,
    actividad_usuarios = [],
    facturacion_sri = null,
    servicios_incluidos_en_totales = false
  } = data || {};

  const metodoPagoIcon = (m) => {
    const icons = { efectivo: 'bi-cash-coin', tarjeta: 'bi-credit-card', transferencia: 'bi-bank' };
    return icons[m] || 'bi-wallet2';
  };

  // Desglose productos/servicios bajo un KPI — solo si el flag está activo y hay servicios
  const desgloseVentas = (obj) => {
    const d = obj?.desglose;
    if (!servicios_incluidos_en_totales || !d || parseFloat(d.servicios?.total || 0) <= 0) return null;
    return (
      <small className="text-muted d-block" style={{ fontSize: '0.72rem', marginTop: '2px' }}>
        Prod. {formatCurrency(d.productos?.total || 0)} · Serv. {formatCurrency(d.servicios?.total || 0)}
      </small>
    );
  };

  return (
    <div>
      {/* FIX: Error state shows alert with retry button; renders even if data is null */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => { setError(null); setLoading(true); setRefreshKey(k => k + 1); }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Reintentar
          </button>
        </div>
      )}
      {/* FIX: If data failed to load entirely, show empty state instead of crashing */}
      {!data && !error && (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-bar-chart-line display-4 d-block mb-3"></i>
          <p>No hay datos disponibles. Intente recargar.</p>
          <button className="btn btn-outline-primary" onClick={() => { setLoading(true); setRefreshKey(k => k + 1); }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Recargar
          </button>
        </div>
      )}

      <div className="d-flex justify-content-end mb-2">
        <button className="btn btn-sm btn-outline-primary" onClick={() => { setLoading(true); setRefreshKey(k => k + 1); }} title="Actualizar datos">
          <i className="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      {/* ─── FILA PRINCIPAL: KPIs según permisos ─── */}
      <div className="row g-3 mb-3">
        {verVentas && (
          <div className="col-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="stat-label mb-1">Ventas Hoy</p>
                  <h3 className="stat-value mb-0">{formatCurrency(ventas_hoy?.total || 0)}</h3>
                </div>
                <div className="stat-icon bg-success bg-opacity-10 text-success"><i className="bi bi-cash-coin"></i></div>
              </div>
              {/* FIX: safe access with fallback for ventas_hoy.cantidad */}
              <small className="text-muted mt-2 d-block">{formatNumber(ventas_hoy?.cantidad || 0)} transacciones</small>
              {desgloseVentas(ventas_hoy)}
            </div>
          </div>
        )}
        {verFinanzasGlobales && (
          <div className="col-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="stat-label mb-1">Ventas del Mes</p>
                  <h3 className="stat-value mb-0">{formatCurrency(ventas_mes?.total || 0)}</h3>
                </div>
                <div className="stat-icon bg-primary bg-opacity-10 text-primary"><i className="bi bi-graph-up-arrow"></i></div>
              </div>
              {/* FIX: safe access with fallback for ventas_mes.cantidad */}
              <small className="text-muted mt-2 d-block">{formatNumber(ventas_mes?.cantidad || 0)} ventas</small>
              {desgloseVentas(ventas_mes)}
            </div>
          </div>
        )}
        <div className="col-6 col-xl-3">
          <div className="stat-card">
            <div className="d-flex justify-content-between">
              <div>
                <p className="stat-label mb-1">Clientes</p>
                {/* FIX: safe access with fallback for clientes.total */}
                <h3 className="stat-value mb-0">{formatNumber(clientes?.total || 0)}</h3>
              </div>
              <div className="stat-icon bg-info bg-opacity-10 text-info"><i className="bi bi-people-fill"></i></div>
            </div>
            {/* FIX: safe access with fallback for clientes.nuevos_mes */}
            <small className="text-success mt-2 d-block">+{formatNumber(clientes?.nuevos_mes || 0)} este mes</small>
          </div>
        </div>
        {verOrdenes && (
          <div className="col-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="stat-label mb-1">Órdenes Pendientes</p>
                  {/* FIX: safe access with fallback for ordenes_pendientes.total */}
                  <h3 className="stat-value mb-0 text-warning">{formatNumber(ordenes_pendientes?.total || 0)}</h3>
                </div>
                <div className="stat-icon bg-warning bg-opacity-10 text-warning"><i className="bi bi-clipboard-check"></i></div>
              </div>
              {ordenes_pendientes?.detalle?.length > 0 && (
                <small className="text-muted mt-2 d-block">
                  {ordenes_pendientes.detalle.map(d => `${d.cantidad} ${d.estado}`).join(', ')}
                </small>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── FILA SECUNDARIA: Inventario + Compras + Alertas (solo con permisos) ─── */}
      {(verInventarioValor || verCompras) && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="stat-label mb-1">Productos</p>
                  {/* FIX: safe access with fallback for inventario.total_productos */}
                  <h3 className="stat-value mb-0">{formatNumber(inventario?.total_productos || 0)}</h3>
                </div>
                <div className="stat-icon bg-secondary bg-opacity-10 text-secondary"><i className="bi bi-box-seam"></i></div>
              </div>
              {/* FIX: safe access with fallback for inventario.total_unidades */}
              <small className="text-muted mt-2 d-block">{formatNumber(inventario?.total_unidades || 0)} unidades</small>
            </div>
          </div>
          {verFinanzasGlobales && (
            <div className="col-6 col-xl-3">
              <div className="stat-card">
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="stat-label mb-1">Valor Inventario</p>
                    <h3 className="stat-value mb-0">{formatCurrency(inventario?.valor_venta || 0)}</h3>
                  </div>
                  <div className="stat-icon bg-success bg-opacity-10 text-success"><i className="bi bi-currency-dollar"></i></div>
                </div>
                <small className="text-muted mt-2 d-block">Costo: {formatCurrency(inventario?.valor_compra || 0)}</small>
              </div>
            </div>
          )}
          {verCompras && (
            <div className="col-6 col-xl-3">
              <div className="stat-card">
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="stat-label mb-1">Compras del Mes</p>
                    {/* FIX: safe access with fallback for compras_mes.total_compras */}
                    <h3 className="stat-value mb-0">{formatNumber(compras_mes?.total_compras || 0)}</h3>
                  </div>
                  <div className="stat-icon bg-info bg-opacity-10 text-info"><i className="bi bi-cart-check"></i></div>
                </div>
                <small className="text-muted mt-2 d-block">{formatCurrency(compras_mes?.monto_total || 0)}</small>
              </div>
            </div>
          )}
          <div className="col-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="stat-label mb-1">Alertas Stock</p>
                  {/* FIX: safe parseInt with fallback for stock alert counts */}
                  <h3 className="stat-value mb-0 text-danger">
                    {formatNumber(parseInt(inventario?.productos_stock_bajo || 0, 10) + parseInt(inventario?.productos_sin_stock || 0, 10))}
                  </h3>
                </div>
                <div className="stat-icon bg-danger bg-opacity-10 text-danger"><i className="bi bi-exclamation-triangle"></i></div>
              </div>
              {/* FIX: safe access with fallback for inventario.productos_sin_stock */}
              <small className="text-muted mt-2 d-block">{formatNumber(inventario?.productos_sin_stock || 0)} sin stock</small>
            </div>
          </div>
        </div>
      )}

      {/* ─── FACTURACIÓN: CON SRI vs SIN SRI ─── */}
      {verFinanzasGlobales && facturacion_sri && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold"><i className="bi bi-receipt-cutoff me-2"></i>Facturación — CON SRI vs SIN SRI</h6>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setVerDetalleSri(v => !v)}>
              <i className={`bi ${verDetalleSri ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
              Detalle diario
            </button>
          </div>
          <div className="card-body">
            {facturacion_sri.error && (
              <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
                <i className="bi bi-exclamation-octagon-fill me-2"></i>
                <span>No se pudieron calcular estas cifras. <strong>Los valores de abajo no son datos reales</strong> — recargue el dashboard.</span>
              </div>
            )}
            <div className="row g-3">
              {[
                { k: 'con', titulo: 'CON SRI', sub: 'Factura AUTORIZADA por el SRI', color: '#198754', badge: 'success', icono: 'bi-patch-check' },
                { k: 'sin', titulo: 'SIN SRI', sub: 'Sin comprobante electrónico', color: '#fd7e14', badge: 'warning', icono: 'bi-receipt' }
              ].map(g => {
                const hoy = facturacion_sri.hoy?.[g.k] || {};
                const mes = facturacion_sri.mes?.[g.k] || {};
                const pendiente = (parseFloat(mes.vendido || 0) - parseFloat(mes.cobrado || 0));
                return (
                  <div key={g.k} className="col-12 col-lg-6">
                    <div className="stat-card h-100" style={{ borderLeft: `4px solid ${g.color}` }}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <span className={`badge bg-${g.badge} bg-opacity-10 text-${g.badge} fw-bold`}>{g.titulo}</span>
                          <small className="text-muted d-block mt-1">{g.sub}</small>
                        </div>
                        <div className={`stat-icon bg-${g.badge} bg-opacity-10 text-${g.badge}`}><i className={`bi ${g.icono}`}></i></div>
                      </div>
                      <div className="row g-2 text-center">
                        <div className="col-6 border-end">
                          <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '.03em' }}>Hoy</small>
                          <h4 className="mb-0" style={{ color: g.color }}>{formatCurrency(hoy.vendido || 0)}</h4>
                          <small className="text-muted">{formatNumber(hoy.transacciones || 0)} transaccion{parseInt(hoy.transacciones || 0, 10) === 1 ? '' : 'es'}</small>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '.03em' }}>Este mes</small>
                          <h4 className="mb-0" style={{ color: g.color }}>{formatCurrency(mes.vendido || 0)}</h4>
                          <small className="text-muted">{formatNumber(mes.transacciones || 0)} transaccion{parseInt(mes.transacciones || 0, 10) === 1 ? '' : 'es'}</small>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                        <small className="text-muted" title="Cobrado hasta hoy sobre las ventas emitidas este mes. No es la caja del mes: no incluye cobros de ventas de meses anteriores.">
                          Cobrado de estas ventas <i className="bi bi-info-circle" style={{ fontSize: '0.7rem' }}></i>
                        </small>
                        <div className="text-end">
                          <strong className="text-success">{formatCurrency(mes.cobrado || 0)}</strong>
                          {pendiente > 0.009 && (
                            <small className="text-danger d-block" style={{ fontSize: '0.72rem' }}>
                              {formatCurrency(pendiente)} por cobrar
                            </small>
                          )}
                        </div>
                      </div>
                      {g.k === 'sin' && facturacion_sri.mes?.servicios && parseInt(facturacion_sri.mes.servicios.transacciones || 0, 10) > 0 && (
                        <small className="text-muted d-block mt-2" style={{ fontSize: '0.72rem' }}>
                          Incluye {formatNumber(facturacion_sri.mes.servicios.transacciones)} servicio{parseInt(facturacion_sri.mes.servicios.transacciones, 10) === 1 ? '' : 's'} (comprobante CS-) por {formatCurrency(facturacion_sri.mes.servicios.vendido || 0)}
                        </small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Ventas con comprobante generado pero NO autorizado por el SRI (devuelto/error/pendiente).
                No pueden contarse como emitidas ni como "solo comprobante interno". */}
            {parseInt(facturacion_sri.mes?.pendiente?.transacciones || 0, 10) > 0 && (
              <div className="alert alert-danger d-flex align-items-start mt-3 mb-0" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                <div>
                  <strong>{formatNumber(facturacion_sri.mes.pendiente.transacciones)} venta{parseInt(facturacion_sri.mes.pendiente.transacciones, 10) === 1 ? '' : 's'} de este mes por {formatCurrency(facturacion_sri.mes.pendiente.vendido || 0)} tienen comprobante generado pero el SRI NO lo autorizó.</strong>
                  <div className="small mt-1">
                    No están declaradas. Revíselas en <Link to="/facturacion-electronica">Facturación Electrónica</Link>.
                    No se cuentan ni en CON SRI ni en SIN SRI.
                  </div>
                </div>
              </div>
            )}

            {verDetalleSri && (
              <div className="mt-3">
                <small className="text-muted fw-semibold d-block mb-2">
                  Transacciones por día — <strong>últimos 30 días</strong>
                  <span className="text-warning ms-1">(ventana distinta a las tarjetas de arriba, que son del mes en curso)</span>
                </small>
                {Array.isArray(facturacion_sri.serie) && facturacion_sri.serie.length > 0 ? (
                  <div className="table-responsive" style={{ maxHeight: 280, overflowY: 'auto' }}>
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>Fecha</th>
                          <th className="text-center text-success">Trans. CON SRI</th>
                          <th className="text-end text-success">Monto CON SRI</th>
                          <th className="text-center text-warning">Trans. SIN SRI</th>
                          <th className="text-end text-warning">Monto SIN SRI</th>
                          <th className="text-center text-danger">Rechazadas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facturacion_sri.serie.map((d, i) => (
                          <tr key={i}>
                            <td className="text-muted">{d?.fecha ? new Date(d.fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', timeZone: 'UTC' }) : '—'}</td>
                            <td className="text-center">{formatNumber(d?.con_transacciones || 0)}</td>
                            <td className="text-end fw-semibold text-success">{formatCurrency(d?.con_vendido || 0)}</td>
                            <td className="text-center">{formatNumber(d?.sin_transacciones || 0)}</td>
                            <td className="text-end fw-semibold text-warning">{formatCurrency(d?.sin_vendido || 0)}</td>
                            <td className="text-center">
                              {parseInt(d?.pend_transacciones || 0, 10) > 0
                                ? <span className="badge bg-danger" title={`${formatCurrency(d?.pend_vendido || 0)} sin autorizar por el SRI`}>{formatNumber(d.pend_transacciones)}</span>
                                : <span className="text-muted">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="empty-state py-3"><i className="bi bi-calendar-x"></i><p>Sin transacciones en los últimos 30 días</p></div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FIX: safe parseInt with fallback on cuentas_por_cobrar fields */}
      {/* ─── CUENTAS POR COBRAR (solo admin/gerente/vendedor con permiso ventas) ─── */}
      {verCxC && cuentas_por_cobrar && (parseInt(cuentas_por_cobrar?.cantidad_cuentas || 0, 10) > 0) && (
        <Link to="/cuentas-por-cobrar" className="text-decoration-none">
          <div className={`alert ${parseInt(cuentas_por_cobrar?.cantidad_vencidas || 0, 10) > 0 ? 'alert-danger' : 'alert-warning'} d-flex align-items-center justify-content-between mb-3`}>
            <div>
              <i className="bi bi-wallet2 me-2"></i>
              <strong>Cuentas por Cobrar:</strong> {formatCurrency(cuentas_por_cobrar?.total_pendiente || 0)} pendiente
              <span className="ms-2">({cuentas_por_cobrar?.cantidad_cuentas || 0} cuenta{parseInt(cuentas_por_cobrar?.cantidad_cuentas || 0, 10) !== 1 ? 's' : ''})</span>
              {parseInt(cuentas_por_cobrar?.cantidad_vencidas || 0, 10) > 0 && (
                <span className="badge bg-danger ms-2">{cuentas_por_cobrar?.cantidad_vencidas || 0} vencida{parseInt(cuentas_por_cobrar?.cantidad_vencidas || 0, 10) !== 1 ? 's' : ''}: {formatCurrency(cuentas_por_cobrar?.total_vencido || 0)}</span>
              )}
            </div>
            <i className="bi bi-arrow-right"></i>
          </div>
        </Link>
      )}

      {/* FIX: safe parseInt/parseFloat with fallback on gastos_mes fields */}
      {/* ─── GASTOS DEL MES (solo admin/gerente con permiso caja o configuracion) ─── */}
      {verFinanzasGlobales && gastos_mes && (parseInt(gastos_mes?.cantidad || 0, 10) > 0 || parseFloat(gastos_mes?.total_pendiente || 0) > 0) && (
        <Link to="/gastos" className="text-decoration-none">
          <div className="alert alert-secondary d-flex align-items-center justify-content-between mb-3">
            <div>
              <i className="bi bi-receipt me-2"></i>
              <strong>Gastos del Mes:</strong> {formatCurrency(gastos_mes?.total_pagado || 0)} pagado
              {parseFloat(gastos_mes?.total_pendiente || 0) > 0 && (
                <span className="badge bg-warning text-dark ms-2">{formatCurrency(gastos_mes?.total_pendiente || 0)} pendiente</span>
              )}
              <span className="ms-2 text-muted">({gastos_mes?.cantidad || 0} gasto{parseInt(gastos_mes?.cantidad || 0, 10) !== 1 ? 's' : ''})</span>
            </div>
            <i className="bi bi-arrow-right"></i>
          </div>
        </Link>
      )}

      {/* ─── GASTOS DIARIOS DEL MES (mig 048, separado de caja chica) ─── */}
      {verFinanzasGlobales && esAdminOGerente() && gastos_diarios_mes && parseInt(gastos_diarios_mes?.cantidad || 0, 10) > 0 && (
        <Link to="/gastos-diarios" className="text-decoration-none">
          <div className="alert alert-secondary d-flex align-items-center justify-content-between mb-3">
            <div>
              <i className="bi bi-journal-text me-2"></i>
              <strong>Gastos Diarios del Mes:</strong> {formatCurrency(gastos_diarios_mes?.total || 0)}
              <span className="ms-2 text-muted">({gastos_diarios_mes?.cantidad || 0} gasto{parseInt(gastos_diarios_mes?.cantidad || 0, 10) !== 1 ? 's' : ''})</span>
              {parseInt(gastos_diarios_mes?.sin_comprobante || 0, 10) > 0 && (
                <span className="badge bg-warning text-dark ms-2">{gastos_diarios_mes.sin_comprobante} sin comprobante</span>
              )}
            </div>
            <i className="bi bi-arrow-right"></i>
          </div>
        </Link>
      )}

      {/* ─── GANANCIAS / UTILIDADES (solo admin/gerente) ─── */}
      {verFinanzasGlobales && ganancias && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-6 col-xl-3">
              <div className="stat-card" style={{borderLeft: '4px solid #198754'}}>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="stat-label mb-1">Ganancia Hoy</p>
                    <h3 className={`stat-value mb-0 ${(ganancias.hoy?.utilidad_neta || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(ganancias.hoy?.utilidad_neta || 0)}
                    </h3>
                  </div>
                  <div className="stat-icon bg-success bg-opacity-10 text-success"><i className="bi bi-graph-up-arrow"></i></div>
                </div>
                <small className="text-muted mt-2 d-block">
                  Margen: <strong className={ganancias.hoy?.margen_neto >= 0 ? 'text-success' : 'text-danger'}>
                    {(ganancias.hoy?.margen_neto || 0).toFixed(1)}%
                  </strong>
                </small>
              </div>
            </div>
            <div className="col-6 col-xl-3">
              <div className="stat-card" style={{borderLeft: '4px solid #0d6efd'}}>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="stat-label mb-1">Ganancia del Mes</p>
                    <h3 className={`stat-value mb-0 ${(ganancias.mes?.utilidad_neta || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(ganancias.mes?.utilidad_neta || 0)}
                    </h3>
                  </div>
                  <div className="stat-icon bg-primary bg-opacity-10 text-primary"><i className="bi bi-trophy"></i></div>
                </div>
                <small className="text-muted mt-2 d-block">
                  Margen: <strong className={ganancias.mes?.margen_neto >= 0 ? 'text-success' : 'text-danger'}>
                    {(ganancias.mes?.margen_neto || 0).toFixed(1)}%
                  </strong>
                </small>
              </div>
            </div>
            <div className="col-6 col-xl-3">
              <div className="stat-card" style={{borderLeft: '4px solid #ffc107'}}>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="stat-label mb-1">Margen Bruto Mes</p>
                    <h3 className="stat-value mb-0 text-warning">
                      {(ganancias.mes?.margen_bruto || 0).toFixed(1)}%
                    </h3>
                  </div>
                  <div className="stat-icon bg-warning bg-opacity-10 text-warning"><i className="bi bi-percent"></i></div>
                </div>
                <small className="text-muted mt-2 d-block">
                  Bruta: {formatCurrency(ganancias.mes?.utilidad_bruta || 0)}
                </small>
              </div>
            </div>
            <div className="col-6 col-xl-3">
              <div className="stat-card" style={{borderLeft: '4px solid #dc3545'}}>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="stat-label mb-1">Gastos Operativos Mes</p>
                    <h3 className="stat-value mb-0 text-danger">
                      {formatCurrency((ganancias.mes?.gastos_operativos || 0) + (ganancias.mes?.gastos_diarios || 0))}
                    </h3>
                  </div>
                  <div className="stat-icon bg-danger bg-opacity-10 text-danger"><i className="bi bi-receipt-cutoff"></i></div>
                </div>
                <small className="text-muted mt-2 d-block">
                  Caja chica: {formatCurrency(ganancias.mes?.gastos_operativos || 0)} · Diarios: {ganancias.mes?.gastos_diarios_error ? <span className="text-danger">no disponible</span> : formatCurrency(ganancias.mes?.gastos_diarios || 0)}
                </small>
                <small className="text-muted d-block">
                  Costo ventas: {formatCurrency(ganancias.mes?.costo_ventas || 0)}
                </small>
              </div>
            </div>
          </div>

          {/* Mini Estado de Resultados */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold"><i className="bi bi-bar-chart-line me-2"></i>Estado de Resultados — {new Date().toLocaleString('es-EC', { month: 'long', year: 'numeric' })}</h6>
            </div>
            <div className="card-body py-2">
              <div className="row align-items-center">
                <div className="col-md-7">
                  <table className="table table-sm table-borderless mb-0">
                    <tbody>
                      {servicios_incluidos_en_totales && parseFloat(ganancias.mes?.ingresos_servicios || 0) > 0 ? (
                        <>
                          <tr>
                            <td className="text-muted">Ingresos por Productos</td>
                            <td className="text-end text-success">{formatCurrency(ganancias.mes?.ingresos_productos || 0)}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Ingresos por Servicios</td>
                            <td className="text-end text-success">{formatCurrency(ganancias.mes?.ingresos_servicios || 0)}</td>
                          </tr>
                          <tr className="border-top">
                            <td className="fw-semibold">= Ingresos Totales</td>
                            <td className="text-end fw-bold text-success">{formatCurrency(ganancias.mes?.ingresos || 0)}</td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td className="text-muted">Ingresos por Ventas</td>
                          <td className="text-end fw-bold text-success">{formatCurrency(ganancias.mes?.ingresos || 0)}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-muted">(-) Costo de Ventas</td>
                        <td className="text-end text-danger">{formatCurrency(ganancias.mes?.costo_ventas || 0)}</td>
                      </tr>
                      <tr className="border-top">
                        <td className="fw-semibold">= Utilidad Bruta</td>
                        <td className="text-end fw-bold">{formatCurrency(ganancias.mes?.utilidad_bruta || 0)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">(-) Gastos Operativos (caja chica)</td>
                        <td className="text-end text-danger">{formatCurrency(ganancias.mes?.gastos_operativos || 0)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">(-) Gastos Diarios</td>
                        <td className="text-end text-danger">
                          {ganancias.mes?.gastos_diarios_error
                            ? <span title="No se pudieron calcular: la utilidad no los incluye">Error al calcular ⚠</span>
                            : formatCurrency(ganancias.mes?.gastos_diarios || 0)}
                        </td>
                      </tr>
                      <tr className="border-top" style={{backgroundColor: (ganancias.mes?.utilidad_neta || 0) >= 0 ? '#d1e7dd' : '#f8d7da'}}>
                        <td className="fw-bold fs-6">= UTILIDAD NETA</td>
                        <td className={`text-end fw-bold fs-6 ${(ganancias.mes?.utilidad_neta || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                          {formatCurrency(ganancias.mes?.utilidad_neta || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-5">
                  {/* FIX: safe access on ganancias.tendencia with optional chaining */}
                  {ganancias?.tendencia && ganancias.tendencia.length > 0 && (
                    <div>
                      <small className="text-muted fw-semibold d-block mb-2">Tendencia 6 meses</small>
                      <div className="d-flex align-items-end gap-1" style={{height: 80}}>
                        {ganancias.tendencia.map((m, i) => {
                          const maxVal = Math.max(...ganancias.tendencia.map(t => Math.abs(parseFloat(t.utilidad_neta))), 1);
                          const val = parseFloat(m.utilidad_neta) || 0;
                          const height = Math.max(Math.abs(val) / maxVal * 60, 4);
                          const isPositive = val >= 0;
                          return (
                            // FIX: null safety — guard date construction on m.mes
                            <div key={i} className="d-flex flex-column align-items-center flex-grow-1" title={`${m.mes ? new Date(m.mes).toLocaleString('es-EC', {month:'short'}) : '—'}: ${formatCurrency(val)}`}>
                              <div
                                style={{width: '100%', height: height, backgroundColor: isPositive ? '#198754' : '#dc3545', borderRadius: 3, minWidth: 16}}
                              />
                              <small className="text-muted" style={{fontSize: '0.65rem'}}>
                                {m.mes ? new Date(m.mes).toLocaleString('es-EC', {month:'short'}) : '—'}
                              </small>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* FIX: safe array access for actividad_usuarios */}
      {/* ─── ACTIVIDAD DEL EQUIPO HOY ─── */}
      {Array.isArray(actividad_usuarios) && actividad_usuarios.length > 0 && (
        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-primary bg-opacity-10">
                <h6 className="mb-0"><i className="bi bi-people me-2"></i>Actividad del Equipo Hoy</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th className="text-center">Ventas</th>
                        <th className="text-end">Vendido</th>
                        <th className="text-center">Cobros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* FIX: null safety — guard actividad_usuarios array */}
                      {(actividad_usuarios || []).map((u, i) => (
                        <tr key={i}>
                          <td><i className="bi bi-person me-1"></i>{u?.nombre_completo || 'Sin nombre'}</td>
                          <td className="text-center">{formatNumber(u?.ventas_hoy || 0)}</td>
                          <td className="text-end">{formatCurrency(parseFloat(u?.monto_ventas_hoy || 0))}</td>
                          <td className="text-center">{formatNumber(u?.cobros_hoy || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FIX: safe property access on caja_activa and its nested resumen */}
      {/* ─── CAJA ACTIVA (banner) ─── */}
      {caja_activa && (
        <div className="alert alert-info d-flex align-items-center justify-content-between mb-4" role="alert">
          <div>
            <i className="bi bi-safe me-2"></i>
            <strong>Caja Abierta:</strong> {caja_activa?.codigo || '—'} - Cajero: {caja_activa?.cajero || '—'}
            <span className="ms-3">
              Ventas: <strong>{formatNumber(caja_activa?.resumen?.num_ventas || 0)}</strong>
              {' '}por <strong>{formatCurrency(caja_activa?.resumen?.total_ventas || 0)}</strong>
            </span>
            {parseFloat(caja_activa?.resumen?.total_servicios || 0) > 0 && (
              <span className="ms-2">
                + Servicios: <strong>{formatNumber(caja_activa?.resumen?.num_servicios || 0)}</strong>
                {' '}por <strong>{formatCurrency(caja_activa?.resumen?.total_servicios || 0)}</strong>
              </span>
            )}
            <span className="ms-2 text-muted small">
              (Cobrado: {formatCurrency(caja_activa?.resumen?.total_cobrado || 0)})
            </span>
          </div>
          <Link to="/ventas" className="btn btn-sm btn-info">
            <i className="bi bi-cash-coin me-1"></i>Ir a POS
          </Link>
        </div>
      )}

      {verVentas && (
        <div className="row g-4">
          {/* ─── ÚLTIMAS VENTAS ─── */}
          <div className={verFinanzasGlobales ? 'col-lg-8' : 'col-12'}>
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><i className="bi bi-receipt me-2"></i>Últimas Ventas</h5>
                <Link to="/ventas" className="btn btn-sm btn-outline-primary">Ver POS</Link>
              </div>
              <div className="card-body p-0">
                {/* FIX: safe array check and safe property access on each venta row */}
                {Array.isArray(ultimas_ventas) && ultimas_ventas.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead><tr><th>Codigo</th><th>Cliente</th><th>Total</th><th>Metodo</th><th>Fecha</th></tr></thead>
                      <tbody>
                        {/* FIX: null safety */ }
                        {(ultimas_ventas || []).map((v, i) => (
                          <tr key={i}>
                            <td><span className="badge bg-primary bg-opacity-10 text-primary fw-bold">{v?.codigo || '—'}</span></td>
                            <td>{v?.cliente || 'Consumidor Final'}</td>
                            <td><strong className="text-success">{formatCurrency(v?.total || 0)}</strong></td>
                            <td><i className={`bi ${metodoPagoIcon(v?.metodo_pago)} me-1`}></i>{v?.metodo_pago || '—'}</td>
                            <td className="text-muted">{v?.created_at ? new Date(v.created_at).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="empty-state py-4"><i className="bi bi-cart"></i><p>No hay ventas registradas</p></div>}
              </div>
            </div>
          </div>

          {/* ─── TOP PRODUCTOS VENDIDOS (solo financiero/gerencial) ─── */}
          {verFinanzasGlobales && (
            <div className="col-lg-4">
              <div className="card h-100">
                <div className="card-header"><h5 className="mb-0"><i className="bi bi-star me-2"></i>Más Vendidos (30 días)</h5></div>
                <div className="card-body">
                  {/* FIX: safe array check and safe property access on top_productos */}
                  {Array.isArray(top_productos) && top_productos.length > 0 ? top_productos.map((p, i) => (
                    <div key={i} className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        <span className="badge bg-primary rounded-circle me-2" style={{ width: 28, height: 28, lineHeight: '28px' }}>{i + 1}</span>
                        <div>
                          <div className="fw-medium" style={{ fontSize: '0.9rem' }}>{(p?.nombre || 'Sin nombre').substring(0, 25)}{(p?.nombre || '').length > 25 ? '...' : ''}</div>
                          <small className="text-muted">{formatNumber(p?.vendidos || 0)} vendidos</small>
                        </div>
                      </div>
                      <strong className="text-success">{formatCurrency(p?.total_vendido || 0)}</strong>
                    </div>
                  )) : <div className="empty-state"><i className="bi bi-bag"></i><p>Sin datos</p></div>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── MOVIMIENTOS INVENTARIO + CATEGORÍAS (solo con permiso inventario) ─── */}
      {verInventarioValor && (
        <div className="row g-4 mt-2">
          <div className="col-lg-8">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><i className="bi bi-activity me-2"></i>Movimientos de Inventario</h5>
                <Link to="/inventario" className="btn btn-sm btn-outline-primary">Ver todos</Link>
              </div>
              <div className="card-body p-0">
                {/* FIX: safe array check and safe property access on ultimos_movimientos rows */}
                {Array.isArray(ultimos_movimientos) && ultimos_movimientos.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead><tr><th>Producto</th><th>Tipo</th><th>Cantidad</th><th>Fecha</th></tr></thead>
                      <tbody>
                        {(ultimos_movimientos || []).map((m, i) => (
                          <tr key={i}>
                            <td>{m?.producto || '—'}</td>
                            <td><span className={`badge bg-${m?.tipo_movimiento === 'entrada' ? 'success' : m?.tipo_movimiento === 'salida' ? 'danger' : 'warning'}`}>{m?.tipo_movimiento || '—'}</span></td>
                            <td><strong className={m?.tipo_movimiento === 'entrada' ? 'text-success' : 'text-danger'}>{m?.tipo_movimiento === 'entrada' ? '+' : '-'}{m?.cantidad || 0}</strong></td>
                            <td className="text-muted">{m?.created_at ? new Date(m.created_at).toLocaleDateString('es-EC') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="empty-state py-4"><i className="bi bi-inbox"></i><p>No hay movimientos</p></div>}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card h-100">
              <div className="card-header"><h5 className="mb-0"><i className="bi bi-pie-chart me-2"></i>Top Categorías</h5></div>
              <div className="card-body">
                {/* FIX: safe array check and safe property access on top_categorias */}
                {Array.isArray(top_categorias) && top_categorias.length > 0 ? top_categorias.map((c, i) => (
                  <div key={i} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>{c?.nombre || 'Sin categoria'}</span>
                      <span className="text-muted">{formatNumber(c?.total_productos || 0)} productos</span>
                    </div>
                    <div className="progress" style={{ height: '6px' }}>
                      <div className="progress-bar" style={{ width: `${((c?.total_productos || 0) / (top_categorias[0]?.total_productos || 1)) * 100}%`, backgroundColor: '#6b9ac4' }}></div>
                    </div>
                  </div>
                )) : <div className="empty-state"><i className="bi bi-tags"></i><p>Sin datos</p></div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── ACCIONES RÁPIDAS (filtradas por permisos) ─── */}
      <div className="card mt-4">
        <div className="card-header"><h5 className="mb-0"><i className="bi bi-lightning-charge me-2"></i>Acciones Rápidas</h5></div>
        <div className="card-body">
          <div className="row g-3">
            {verVentas && <div className="col-6 col-md-4 col-xl-2"><Link to="/ventas" className="btn btn-outline-success w-100 py-3"><i className="bi bi-cash-coin d-block fs-4 mb-2"></i>Nueva Venta</Link></div>}
            {tienePermiso('clientes') && <div className="col-6 col-md-4 col-xl-2"><Link to="/clientes" className="btn btn-outline-primary w-100 py-3"><i className="bi bi-person-plus d-block fs-4 mb-2"></i>Clientes</Link></div>}
            {verOrdenes && <div className="col-6 col-md-4 col-xl-2"><Link to="/ordenes-trabajo" className="btn btn-outline-warning w-100 py-3"><i className="bi bi-clipboard-check d-block fs-4 mb-2"></i>Órdenes</Link></div>}
            {tienePermiso('historia_clinica') && <div className="col-6 col-md-4 col-xl-2"><Link to="/historia-clinica/nueva" className="btn btn-outline-info w-100 py-3"><i className="bi bi-clipboard2-pulse d-block fs-4 mb-2"></i>Historia Clínica</Link></div>}
            {tienePermiso('productos') && <div className="col-6 col-md-4 col-xl-2"><Link to="/productos" className="btn btn-outline-secondary w-100 py-3"><i className="bi bi-plus-circle d-block fs-4 mb-2"></i>Productos</Link></div>}
            {verCompras && <div className="col-6 col-md-4 col-xl-2"><Link to="/compras" className="btn btn-outline-dark w-100 py-3"><i className="bi bi-cart-plus d-block fs-4 mb-2"></i>Compras</Link></div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
